import { useCallback, useState } from "react";
import {
  GetAllInvoicesUseCase,
  InvoiceEntity,
  InvoiceListStatus,
  PaginationInfo,
} from "../../domain";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";

const invoiceRepository = new InvoiceRepositoryImpl();
const getAllInvoicesUseCase = new GetAllInvoicesUseCase(invoiceRepository);

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  status?: InvoiceListStatus;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: "created_at" | "updated_at" | "filename" | "status" | "original_filename";
  sortOrder?: "asc" | "desc";
}

export const useInvoiceList = () => {
  const [invoices, setInvoices] = useState<InvoiceEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoices = useCallback(async (params: InvoiceListParams = {}) => {
    setLoading(true);
    setError(null);

    const limit = params.limit ?? 10;
    const page = params.page ?? 1;

    try {
      const result = await getAllInvoicesUseCase.execute({
        limit,
        offset: Math.max(page - 1, 0) * limit,
        status: params.status ?? "all",
        search: params.search?.trim() || undefined,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        sort_by: params.sortBy ?? "created_at",
        sort_order: params.sortOrder ?? "desc",
      });

      setInvoices(result.invoices);
      setPagination(result.pagination);
    } catch (error: any) {
      setError(error.message || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    invoices,
    pagination,
    loading,
    error,
    fetchInvoices,
    clearError,
  };
};
